// Shared ECharts chart settings: colours from the CSS theme, axes, tooltips.
import type { Model } from "../core/types";
import { esc, fmtDate, fmtNum, msDay } from "./format";
import { app } from "./state.svelte";

// Chart options are assembled from loose objects (ECharts accepts very varied shapes).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Opt = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TipParam = any;

export const cssVar = (n: string): string =>
  typeof document === "undefined" ? "" : getComputedStyle(document.documentElement).getPropertyValue(n).trim();
/** Colour of series k (8 theme colours). Depends on the theme. */
export const series = (k: number): string => {
  void app.theme;
  return cssVar(`--s${(k % 8) + 1}`);
};

export interface Palette {
  ink: string; ink2: string; muted: string; grid: string; axis: string; surface: string; border: string;
  gain: string; loss: string; gainText: string; lossText: string; accent: string; surface2: string;
}
export function palette(): Palette {
  void app.theme; // switching the theme recomputes chart options
  return {
    ink: cssVar("--ink"), ink2: cssVar("--ink-2"), muted: cssVar("--muted"), grid: cssVar("--grid"), axis: cssVar("--axis"),
    surface: cssVar("--surface"), border: cssVar("--border"), gain: cssVar("--gain-mark"), loss: cssVar("--loss-mark"),
    gainText: cssVar("--gain"), lossText: cssVar("--loss"), accent: cssVar("--accent"), surface2: cssVar("--surface-2"),
  };
}

/** Amount on a chart axis: "12 tys.", "1,2 mln"; hidden in privacy mode. */
export const compactMoney = (v: number): string => {
  if (app.privacy) return "•••";
  const a = Math.abs(v);
  if (a >= 1e6) return fmtNum(v / 1e6, 1) + " mln";
  if (a >= 1e3) return fmtNum(v / 1e3, 0) + " tys.";
  return fmtNum(v, 0);
};
/** Amount in a chart tooltip (privacy mode: •••). */
export const tipMoney = (v: number, d = 0): string => (app.privacy ? "•••" : `${fmtNum(v, d)} zł`);

export function baseOption(p: Palette = palette()): Opt {
  return {
    animation: false,
    textStyle: { fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif", color: p.ink2 },
    grid: { left: 8, right: 18, top: 34, bottom: 6, containLabel: true },
    tooltip: {
      trigger: "axis", backgroundColor: p.surface, borderColor: p.border, borderWidth: 1, padding: [8, 10],
      textStyle: { color: p.ink, fontSize: 12 }, axisPointer: { type: "line", lineStyle: { color: p.axis } }, confine: true,
    },
    legend: { top: 0, left: 0, icon: "roundRect", itemWidth: 14, itemHeight: 3, itemGap: 16, textStyle: { color: p.ink2, fontSize: 12 } },
  };
}
export function timeAxis(p: Palette = palette()): Opt {
  return { type: "time", axisLine: { lineStyle: { color: p.axis } }, axisTick: { show: false }, axisLabel: { color: p.muted, hideOverlap: true }, splitLine: { show: false } };
}
export function valueAxis(fmt: (v: number) => string, p: Palette = palette(), extra: Opt = {}): Opt {
  return { type: "value", scale: false, axisLabel: { color: p.muted, formatter: fmt }, splitLine: { lineStyle: { color: p.grid } }, axisLine: { show: false }, ...extra };
}
export function categoryAxis(data: string[], p: Palette, labelExtra: Opt = {}, extra: Opt = {}): Opt {
  return { type: "category", data, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } }, axisLabel: { color: p.muted, ...labelExtra }, ...extra };
}
/** Tooltip listing series: header (date or category) and "series — value" rows. */
export function tooltipRows(params: TipParam, fmt: (v: number, x: TipParam) => string): string {
  const list = Array.isArray(params) ? params : [params];
  if (!list.length) return "";
  const d = list[0].axisValueLabel || list[0].axisValue;
  const head = typeof list[0].axisValue === "number" && list[0].axisType !== "category" && list[0].axisValue > 1e11 ? fmtDate(msDay(list[0].axisValue)) : esc(d);
  return `<div style="font-weight:600;margin-bottom:4px">${head}</div>` + list.filter((x: TipParam) => x.value !== undefined && x.value !== null).map((x: TipParam) => {
    const v = Array.isArray(x.value) ? x.value[1] : x.value;
    return `<div style="display:flex;gap:14px;justify-content:space-between"><span>${x.marker}${esc(x.seriesName)}</span><b>${fmt(v, x)}</b></div>`;
  }).join("");
}
/** Axis tooltip with a value formatter (shorthand). */
export const axisTip = (p: Palette, fmt: (v: number, x: TipParam) => string, extra: Opt = {}): Opt => ({
  ...baseOption(p).tooltip, ...extra, formatter: (ps: TipParam) => tooltipRows(ps, fmt),
});
/** Single-item tooltip (bar, point, pie slice). */
export const itemTip = (p: Palette, formatter: (x: TipParam) => string): Opt => ({
  trigger: "item", backgroundColor: p.surface, borderColor: p.border, textStyle: { color: p.ink }, formatter,
});

export function lineSeries(name: string, data: unknown[], color: string, extra: Opt = {}): Opt {
  return { name, type: "line", data, showSymbol: false, symbol: "circle", symbolSize: 8, lineStyle: { width: 2, color }, itemStyle: { color }, emphasis: { disabled: true }, ...extra };
}
/** [day, value] points of a daily array from s0 to e. */
export function daySeries(m: Pick<Model, "days">, arr: ArrayLike<number>, s0: number, e: number, fn: (v: number, i: number) => number | null = (v) => v): [string, number | null][] {
  const out: [string, number | null][] = [];
  for (let i = s0; i <= e; i++) out.push([m.days[i], fn(arr[i], i)]);
  return out;
}
/** Gain/loss bar: colour and corner rounding depend on the sign. */
export const signBar = (v: number, p: Palette, horizontal = false, posColor = p.gain): Opt => ({
  value: v,
  itemStyle: { color: v >= 0 ? posColor : p.loss, borderRadius: horizontal ? (v >= 0 ? [0, 4, 4, 0] : [4, 0, 0, 4]) : v >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4] },
});

/** Stable colour per instrument (in order of first appearance). */
const instrMap = new Map<string, number>();
export const instrColor = (key: string): string => {
  if (!instrMap.has(key)) instrMap.set(key, instrMap.size);
  return series(instrMap.get(key) as number);
};
